import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Volume2, Globe } from 'lucide-react';

const skills = [
  {
    id: 'memoriser',
    label: 'Je me souviens de tout',
    question: 'Où en est le dossier Martin ?',
    response: 'Le client a validé le devis mardi. Justine a envoyé le contrat mercredi. Dernière action : relance envoyée vendredi. Il doit revenir lundi avec une réponse.',
    tag: 'Mémoriser',
    audio: '/audio/skills/memoriser.mp3',
  },
  {
    id: 'comprendre',
    label: 'Je comprends ce qui se passe',
    question: 'Il y a des problèmes en cours ?',
    response: 'Signal détecté : le client Morel montre des signes d\'insatisfaction depuis 3 échanges. Ton plus sec, délais de réponse plus longs.',
    followUp: 'Action suggérée : une relance personnalisée avec un point sur les délais pourrait rétablir la relation.',
    tag: 'Comprendre',
    audio: '/audio/skills/comprendre.mp3',
  },
  {
    id: 'restituer',
    label: 'J\'écris comme vos collaborateurs',
    question: 'Rédige une relance pour Morel, comme si c\'était Justine.',
    response: 'Bonjour M. Morel, je reviens vers vous suite à notre dernier échange. Je comprends vos interrogations sur le délai — je m\'en occupe personnellement et vous envoie une mise à jour demain matin. Bien à vous, Justine',
    tag: 'Restituer',
    audio: '/audio/skills/restituer.mp3',
  },
  {
    id: 'interagir',
    label: 'Vous pouvez me poser des questions',
    question: 'Explique-moi ce dossier comme si j\'étais le client.',
    response: 'Votre commande a été validée le 12 mars. La livraison est prévue le 28. Un retard de 3 jours a été signalé par le fournisseur, votre interlocuteur vous a envoyé un mail mardi pour vous prévenir.',
    tag: 'Interagir',
    audio: '/audio/skills/interagir.mp3',
  },
  {
    id: 'agir',
    label: 'Je vous suggère quoi faire',
    question: 'Quoi faire avec le client Morel ?',
    response: 'Voici ce que je te recommande de faire. Le client montre des signes d\'insatisfaction depuis 3 échanges. Je suggère une relance personnalisée avec un point sur les délais et un geste commercial.',
    followUp: 'J\'ai préparé un message prêt à envoyer dans le style de Justine. Tu veux que je te le montre ?',
    tag: 'Proposer',
    audio: '/audio/skills/agir.mp3',
  },
];

const TypingText = ({ text, speed = 20, onComplete }) => {
  const [displayed, setDisplayed] = useState('');
  const indexRef = useRef(0);

  useEffect(() => {
    setDisplayed('');
    indexRef.current = 0;
    const interval = setInterval(() => {
      if (indexRef.current < text.length) {
        setDisplayed(text.slice(0, indexRef.current + 1));
        indexRef.current++;
      } else {
        clearInterval(interval);
        onComplete?.();
      }
    }, speed);
    return () => clearInterval(interval);
  }, [text, speed, onComplete]);

  return (
    <span>
      {displayed}
      {displayed.length < text.length && (
        <span className="inline-block w-0.5 h-4 bg-[#3b82f6] ml-0.5 animate-pulse align-middle" />
      )}
    </span>
  );
};

const AudioWave = () => (
  <div className="flex items-center gap-0.5">
    {[...Array(4)].map((_, i) => (
      <motion.div
        key={i}
        className="w-1 bg-[#3b82f6] rounded-full"
        animate={{ height: [6, 14, 6] }}
        transition={{ duration: 0.5, repeat: Infinity, delay: i * 0.1, ease: "easeInOut" }}
      />
    ))}
  </div>
);

const CloneSkillsBlock = () => {
  const [activeSkill, setActiveSkill] = useState(0);
  const [phase, setPhase] = useState('idle');
  const [hasInteracted, setHasInteracted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const blockRef = useRef(null);
  const audioRef = useRef(null);

  const current = skills[activeSkill];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasInteracted) {
          setHasInteracted(true);
          setPhase('question');
        }
      },
      { threshold: 0.3 }
    );
    if (blockRef.current) observer.observe(blockRef.current);
    return () => observer.disconnect();
  }, [hasInteracted]);

  // Cleanup audio on unmount or skill change
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    setIsPlaying(false);
  };

  const handleSkillClick = (index) => {
    if (index === activeSkill && phase !== 'idle') return;
    stopAudio();
    setActiveSkill(index);
    setPhase('question');
  };

  const toggleAudio = () => {
    if (isPlaying) {
      stopAudio();
      return;
    }

    const audio = new Audio(current.audio);
    audioRef.current = audio;
    
    audio.addEventListener('ended', () => {
      setIsPlaying(false);
      audioRef.current = null;
    });
    
    audio.play().catch(() => {});
    setIsPlaying(true);
  };

  return (
    <motion.div
      ref={blockRef}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="rounded-2xl border border-white/5 bg-gradient-to-b from-[#0f0f17] to-[#09090b] overflow-hidden"
      data-testid="clone-skills-block"
    >
      {/* Header */}
      <div className="p-6 md:p-8 border-b border-white/5">
        <p className="text-[#71717a] text-sm mb-2">Voici concrètement ce que ça change.</p>
        <p className="text-[#f5f5f5] text-xl md:text-2xl font-semibold">
          Je suis un clone collaborateur.{' '}
          <span className="text-[#3b82f6]">Voici ce que je sais faire.</span>
        </p>
      </div>

      {/* Skill Tabs */}
      <div className="flex overflow-x-auto gap-1 p-3 md:p-4 border-b border-white/5 scrollbar-hide" data-testid="clone-skills-tabs">
        {skills.map((skill, index) => (
          <button
            key={skill.id}
            onClick={() => handleSkillClick(index)}
            className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeSkill === index
                ? 'bg-[#3b82f6] text-white'
                : 'bg-[#1a1a2e]/50 text-[#71717a] hover:text-[#a1a1aa] hover:bg-[#1a1a2e]'
            }`}
            data-testid={`skill-tab-${skill.id}`}
          >
            {skill.tag}
          </button>
        ))}
      </div>

      {/* Chat Area */}
      <div className="p-6 md:p-8 min-h-[280px]" data-testid="clone-skills-chat">
        <AnimatePresence mode="wait">
          <motion.div
            key={`${activeSkill}-${phase}`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="space-y-5"
          >
            {/* Skill label */}
            <p className="text-[#3b82f6] text-sm font-medium" data-testid="clone-skill-label">
              {current.label}
            </p>

            {/* Question */}
            {(phase === 'question' || phase === 'response' || phase === 'followUp') && (
              <div className="flex justify-end">
                <div className="bg-[#1a1a2e] rounded-2xl rounded-tr-md px-5 py-3 max-w-[85%]">
                  <p className="text-[#f5f5f5] text-sm">
                    {phase === 'question' ? (
                      <TypingText 
                        text={current.question} 
                        speed={25} 
                        onComplete={() => setTimeout(() => setPhase('response'), 400)} 
                      />
                    ) : (
                      current.question
                    )}
                  </p>
                </div>
              </div>
            )}

            {/* Response */}
            {(phase === 'response' || phase === 'followUp') && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="flex justify-start"
              >
                <div className="bg-[#3b82f6]/10 border border-[#3b82f6]/20 rounded-2xl rounded-tl-md px-5 py-3 max-w-[85%]">
                  <p className="text-[#e4e4e7] text-sm leading-relaxed">
                    {phase === 'response' ? (
                      <TypingText 
                        text={current.response} 
                        speed={15} 
                        onComplete={() => {
                          if (current.followUp) {
                            setTimeout(() => setPhase('followUp'), 500);
                          }
                        }}
                      />
                    ) : (
                      current.response
                    )}
                  </p>
                </div>
              </motion.div>
            )}

            {/* Follow-up response */}
            {phase === 'followUp' && current.followUp && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="flex justify-start"
              >
                <div className="bg-[#3b82f6]/15 border border-[#3b82f6]/25 rounded-2xl rounded-tl-md px-5 py-3 max-w-[85%]">
                  <p className="text-[#e4e4e7] text-sm leading-relaxed">
                    <TypingText text={current.followUp} speed={15} />
                  </p>
                </div>
              </motion.div>
            )}

            {/* Audio Player — appears after response */}
            {(phase === 'response' || phase === 'followUp') && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, delay: 0.3 }}
                className="flex justify-start"
              >
                <button
                  onClick={toggleAudio}
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-full border transition-all ${
                    isPlaying 
                      ? 'bg-[#3b82f6]/15 border-[#3b82f6]/30 text-[#3b82f6]' 
                      : 'bg-[#1a1a2e]/50 border-white/10 text-[#71717a] hover:text-[#a1a1aa] hover:border-white/20'
                  }`}
                  data-testid="skill-audio-button"
                >
                  {isPlaying ? (
                    <>
                      <AudioWave />
                      <span className="text-xs font-medium">Écouter la voix du clone</span>
                    </>
                  ) : (
                    <>
                      <Volume2 className="w-4 h-4" />
                      <span className="text-xs font-medium">Écouter la voix du clone</span>
                    </>
                  )}
                </button>
              </motion.div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Footer - Voix + Multilingue */}
      <div className="px-6 md:px-8 pb-6 md:pb-8 flex flex-col sm:flex-row gap-4 sm:gap-8" data-testid="clone-skills-footer">
        <div className="flex items-center gap-3 text-[#71717a] text-sm">
          <div className="w-8 h-8 rounded-full bg-[#1a1a2e] border border-white/5 flex items-center justify-center">
            <Volume2 className="w-3.5 h-3.5" />
          </div>
          <span>Échangez à l'oral, avec la voix du collaborateur.</span>
        </div>
        <div className="flex items-center gap-3 text-[#71717a] text-sm">
          <div className="w-8 h-8 rounded-full bg-[#1a1a2e] border border-white/5 flex items-center justify-center">
            <Globe className="w-3.5 h-3.5" />
          </div>
          <span>Justine ne parle pas anglais. <span className="text-[#a1a1aa]">Son clone, oui.</span></span>
        </div>
      </div>

      {/* Phrase clé */}
      <div className="px-6 md:px-8 pb-8 md:pb-10">
        <p className="text-[#f5f5f5] font-medium text-center">
          Comme si vous échangiez directement avec votre collaborateur.
        </p>
      </div>
    </motion.div>
  );
};

export default CloneSkillsBlock;
